import { useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useT } from "../../../i18n/I18nProvider";
import { NAV_DRAWER_MAX } from "../../shell/navDrawer";
import { useToast, useViewportSize } from "../../ui";
import { RecipientField, asRecipients, type Recipient } from "./RecipientField";
import { CopyRows, CopyToggles, useCopyRecipients } from "./CopyRecipients";
import { geometry, type WindowState } from "./composerWindow";
import { AttachmentTray, useComposerAttachments } from "../_shared/attachments";
import { ComposerFoot } from "./ComposerFoot";
import { ComposerTitleBar } from "./ComposerTitleBar";
import { SignatureBlock, useMailSignatures, type SignatureChoice } from "./SignaturePick";
import { MyMailDialog } from "./signatures";
import { FromRow, senderLine, useMailSenders } from "./SenderPick";
import { RichTextEditor } from "../../../components/RichTextEditor";
import { htmlToText } from "../../../emailBuilder/text";
import { isEmptyHtml, textToHtml } from "./richBody";
import type { DraftContent } from "../../../lib/mailDrafts";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { ScheduleModal, useDraftAutosave, type DraftAutosave } from "./drafts";

export type ComposerMode = "new" | "reply" | "forward";

export interface ComposerDraft {
  mode: ComposerMode;
  /** Set on a reply: the thread the message is appended to. */
  threadId?: string;
  to: string[];
  subject: string;
  /** Plain text poured into the editor - a forward's quoted original. */
  body: string;
  /** Files already staged for a new letter, e.g. a quote's PDF. */
  attachments?: { id: string; name: string; size: number }[];
  /** The sales document this letter carries, stamped sent once the letter goes. */
  salesDoc?: { kind: "estimate"; id: string };
  /**
   * The address a fresh letter starts from: the mailbox it was written in, so
   * a letter begun in kristi@'s folder tree leaves from kristi@. Absent: the
   * writer's default sender. A saved draft's own sender wins over this.
   */
  startFrom?: string;
  /** A saved draft being continued (from Drafts): its id and all it held. */
  saved?: {
    id: string;
    cc: string[];
    bcc: string[];
    bodyHtml: string;
    signature: SignatureChoice | undefined;
    /** The sender it was saved with. Absent: the default. */
    fromAddress?: string;
    attachments: { id: string; name: string; size: number }[];
  };
}

/**
 * What travels with a send besides the letter: when to send it, and a handle on
 * the draft that has been saving it, so the caller can finish that draft - make
 * sure the last word is saved before scheduling it, and stop the saving once
 * the letter has gone.
 */
export interface ComposerSendExtra {
  /** Set: schedule for this instant instead of sending now. */
  scheduleAt?: string;
  draft: Pick<DraftAutosave, "flush" | "stop">;
}

export interface ComposerValues {
  to: string[];
  cc: string[];
  /** Blind copies: they get the letter, and nobody else on it learns that. */
  bcc: string[];
  subject: string;
  body: string;
  /**
   * Files already uploaded (`attachments/useComposerAttachments`). The id is
   * what the send names; the name is here so the confirmation can list what is
   * going out without asking the server what it is about to be given.
   */
  attachments: { id: string; name: string }[];
  /**
   * The message as the editor built it.
   *
   * `body` above stays the plain-text version, which is what the mail's text
   * alternative carries and what the confirmation shows. Sending both is what
   * lets every older caller of the send route keep working: without `bodyHtml`
   * the server wraps the text exactly as it always did.
   */
  bodyHtml: string;
  /**
   * Which saved signature to append, `null` for none.
   *
   * Three states reach the wire, and the third one matters: leaving this out
   * asks for the mailbox default, which is what every caller written before the
   * picker existed does. The composer always has an opinion, so it always sends
   * one of the first two.
   */
  signatureId: string | null;
  /**
   * The address the letter leaves from, always named once the list has loaded
   * so the server sends exactly what the confirmation showed. Absent only when
   * the list could not be read: the server then uses the default sender.
   */
  fromAddress?: string;
  /** The From line as the recipient will see it, for the confirmation. */
  fromLine: string;
}

/**
 * The mail composer, docked in the corner rather than thrown up as a modal.
 *
 * Writing a reply must not stop you reading the thread you are answering, which
 * is the whole reason the pane behind stays live. At full size that stops being
 * true - the window then covers the screen and earns a backdrop; docked it has
 * none, on purpose.
 */
export function Composer({
  draft,
  sending,
  aiBusy,
  onClose,
  onSend,
  onDraftSaved,
  onAi,
  fromAddress,
  siteId,
}: {
  draft: ComposerDraft;
  sending: boolean;
  aiBusy?: boolean;
  onClose: () => void;
  onSend: (values: ComposerValues, extra: ComposerSendExtra) => void;
  /** Every time the draft behind the window is saved. */
  onDraftSaved?: () => void;
  /** Absent on a new message: there is no thread for the model to read. */
  onAi?: () => Promise<string | null>;
  /** The default sending address, shown until the sender list has loaded. */
  fromAddress: string;
  /** The host site whose media library the clip may offer. Absent for an
   *  operator with no host site, and the clip then offers the disk only. */
  siteId?: string | null;
}) {
  const t = useT();
  // `useViewportSize`, not `window.innerHeight`: on a phone this window is the
  // whole screen and its send row is the bottom of it, and `innerHeight` does
  // not shrink when the keyboard opens. Sized off that, Send sat behind ~300px
  // of keyboard for as long as anybody was typing.
  const { vw, vh } = useViewportSize();
  const toast = useToast();
  const [state, setState] = useState<WindowState>("dock");
  // Everything starts from the draft. The caller gives every letter its own
  // mounted window (see `openComposer` in EmailSection), so a saved draft being
  // continued simply is the initial state - there is no previous letter here
  // to clear out first.
  const saved = draft.saved;
  const [to, setTo] = useState<Recipient[]>(() => asRecipients(draft.to));
  const copies = useCopyRecipients(saved ? { cc: saved.cc, bcc: saved.bcc } : undefined);
  const [subject, setSubject] = useState(draft.subject);
  // HTML, because the box is a rich-text editor now. What arrives on a fresh
  // draft is still plain text - a forward's quoted original - so it is poured
  // in; a saved draft kept the editor's own HTML.
  const [body, setBody] = useState(() => (saved ? saved.bodyHtml : textToHtml(draft.body)));
  const [warn, setWarn] = useState(false);
  const tray = useComposerAttachments(saved?.attachments ?? draft.attachments);
  const [scheduling, setScheduling] = useState(false);
  const [askDiscard, setAskDiscard] = useState(false);

  // The signature the letter will carry. `undefined` while the list is still
  // being fetched, so the first render does not claim "no signature" and then
  // change its mind under the writer's eyes.
  const signatures = useMailSignatures();
  const [chosen, setChosen] = useState<SignatureChoice | undefined>(saved?.signature);
  const signatureId: SignatureChoice =
    chosen !== undefined ? chosen : (signatures?.defaultId || null);
  const setSignatureId = useCallback((v: SignatureChoice) => setChosen(v), []);
  // The writer's own signatures and sender, opened from the signature's menu.
  const [editingMine, setEditingMine] = useState(false);

  // The sender, on the signature's pattern: `undefined` until the writer (or
  // the saved draft) picks one, so the draft does not pin the default address
  // and a later change of default still reaches a letter nobody set.
  const senders = useMailSenders();
  const [from, setFrom] = useState<string | undefined>(saved?.fromAddress ?? draft.startFrom);
  const fromAddressNow = from ?? senders?.defaultAddress;
  const fromSender = senders?.rows.find((r) => r.address === fromAddressNow);

  const geo = useMemo(() => geometry(state, vw, vh), [state, vw, vh]);
  // Full screen already, so there is nothing for maximize to do and no room to
  // spend on a button that does nothing.
  const phone = vw <= NAV_DRAWER_MAX;

  // What the mail's plain-text alternative and the confirmation preview read.
  // Derived rather than kept beside the HTML: two copies of one message drift,
  // and the copy that drifts is the one nobody is looking at.
  const bodyText = htmlToText(body);

  // The draft the window saves itself into. Nothing is created until the
  // letter differs from how the window opened: a reply opened and closed again,
  // or a forward nobody touched, is not a draft anybody wrote.
  const content: DraftContent = {
    mode: draft.mode,
    threadId: draft.threadId ?? null,
    to: to.map((r) => r.address),
    cc: copies.list.cc.map((r) => r.address),
    bcc: copies.list.bcc.map((r) => r.address),
    subject,
    bodyHtml: body,
    bodyText,
    ...(chosen !== undefined ? { signatureId: chosen } : {}),
    ...(from !== undefined ? { fromAddress: from } : {}),
    attachmentIds: tray.ready.map((f) => f.id),
  };
  const opened = useRef(JSON.stringify(content));
  const autosave = useDraftAutosave({
    content,
    hasContent: JSON.stringify(content) !== opened.current,
    initialId: saved?.id ?? null,
    paused: sending,
    onSaved: onDraftSaved,
  });

  /**
   * Closing the window keeps the letter: it is saved as a draft (or already
   * was), files included, and Drafts is where it waits. Throwing a letter
   * away is its own button - see `discard`.
   */
  function close() {
    const kept = autosave.draftId() !== null || JSON.stringify(content) !== opened.current;
    onClose();
    if (kept) toast(t("drafts.kept"));
  }

  /** Throw the letter away for good: the draft, and the files staged for it. */
  async function discard() {
    setAskDiscard(false);
    try {
      await autosave.discard();
    } catch {
      toast(t("drafts.discardFailed"));
      return;
    }
    tray.discard();
    onClose();
    toast(t("drafts.discarded"));
  }

  // The title bar carries the subject once there is one: "New message" tells
  // you nothing about which draft you collapsed.
  const title = subject.trim() || t(`compose.${draft.mode === "new" ? "title" : draft.mode}`);

  // Only a reply knows its recipient already. A forward needs one just as much
  // as a new message does - and used to be rendered without the field at all,
  // so "Forward" quietly sent to nobody.
  const needsRecipient = draft.mode !== "reply";

  function submit(scheduleAt?: string) {
    if (isEmptyHtml(body)) {
      setWarn(true);
      setTimeout(() => setWarn(false), 400);
      return;
    }
    if (needsRecipient && to.length === 0) {
      setWarn(true);
      setTimeout(() => setWarn(false), 400);
      return;
    }
    onSend({
      to: to.map((r) => r.address),
      cc: copies.list.cc.map((r) => r.address),
      bcc: copies.list.bcc.map((r) => r.address),
      subject: subject.trim(),
      body: bodyText,
      bodyHtml: body,
      // Only the finished uploads. A file still going up is not a file yet, and
      // the Send button is disabled while any of them are - see `tray.busy`.
      attachments: tray.ready,
      signatureId,
      fromAddress: senders ? fromAddressNow : undefined,
      // A saved draft's sender that has since left the list is named as it is,
      // so the dialog shows what the server is about to refuse.
      fromLine: senderLine(fromSender, fromAddressNow || fromAddress),
    }, { scheduleAt, draft: autosave });
  }


  const shell = (
    <>
      {/* A full-size window stops being a side task, so it gets a backdrop.
          Clicking it hands the window back to the corner rather than closing
          it: nothing typed is at stake in a resize. */}
      <AnimatePresence>
        {state === "max" && !phone ? (
          <motion.div
            className="fixed inset-0 z-[77] bg-[color-mix(in_oklab,var(--color-foreground)_22%,transparent)] backdrop-blur-[9px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setState("dock")}
          />
        ) : null}
      </AnimatePresence>

      <motion.div
        className="fixed z-[78] flex flex-col overflow-hidden bg-[var(--crm-surface)] shadow-[var(--crm-sh-lg)] outline outline-1 -outline-offset-1 outline-[var(--crm-hair)]"
        initial={{ opacity: 0, y: 20, ...geo, borderRadius: geo.corners }}
        animate={{
          opacity: 1,
          y: 0,
          width: geo.width,
          height: geo.height,
          right: geo.right,
          bottom: geo.bottom,
          borderRadius: geo.corners,
        }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.34, ease: [0.32, 0.72, 0, 1] }}
        role="dialog"
        aria-label={title}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            submit();
          }
          if (e.key === "Escape") {
            e.preventDefault();
            close();
          }
        }}
      >
        <ComposerTitleBar
          title={title}
          state={state}
          setState={setState}
          phone={phone}
          close={close}
          save={{ state: autosave.state, savedAt: autosave.savedAt }}
        />

        {state !== "min" ? (
          <>
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <FromRow
                set={senders}
                value={fromAddressNow ?? ""}
                onChange={setFrom}
                fallback={fromAddress}
                disabled={sending}
              />

              {needsRecipient ? (
                <motion.div animate={warn ? { x: [-5, 4, -2, 0] } : { x: 0 }} transition={{ duration: 0.38 }}>
                  <RecipientField label={t("compose.to")} value={to} onChange={setTo} autoFocus />
                </motion.div>
              ) : null}

              <CopyRows copies={copies} />

              <div className="flex min-h-[46px] items-center gap-2.5 border-b border-[var(--crm-hair)] px-[22px]">
                <label htmlFor="crm-comp-subj" className="w-11 flex-none text-xs font-[650] text-muted-foreground">
                  {t("compose.subject")}
                </label>
                <input
                  id="crm-comp-subj"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t("compose.subjectPlaceholder")}
                  className="h-11 min-w-0 flex-1 border-0 bg-transparent text-[13.25px] font-[650] outline-none"
                  autoComplete="off"
                />
                <CopyToggles copies={copies} />
              </div>

              {/* The same editor the page and contract builders use, toolbar
                  and all. A second rich-text box with its own idea of what bold
                  means is how two parts of one product start disagreeing about
                  the HTML they store. */}
              {/* The signature is part of the letter: under the text, in the
                  same scrolling body. Rendered as soon as the list has loaded,
                  empty list included: a control that appears only once a
                  signature exists is a feature nobody finds. */}
              <RichTextEditor
                value={body}
                onChange={setBody}
                placeholder={t("compose.bodyPlaceholder")}
                className="crm-compose-rte"
                after={
                  signatures ? (
                    <SignatureBlock
                      set={signatures}
                      value={signatureId}
                      onChange={setSignatureId}
                      onEdit={() => setEditingMine(true)}
                      disabled={sending}
                    />
                  ) : null
                }
              />
            </div>

            <AttachmentTray tray={tray} />

            <ComposerFoot
              submit={() => submit()}
              onSchedule={() => setScheduling(true)}
              onDiscard={() => (autosave.draftId() || JSON.stringify(content) !== opened.current ? setAskDiscard(true) : void discard())}
              sending={sending}
              tray={tray}
              siteId={siteId}
              onAi={onAi}
              aiBusy={aiBusy}
              setBody={setBody}
            />
          </>
        ) : null}
      </motion.div>

      {scheduling ? (
        <ScheduleModal
          onClose={() => setScheduling(false)}
          onPick={(iso) => {
            setScheduling(false);
            submit(iso);
          }}
        />
      ) : null}

      {/* Beside the window, not inside it: the window's own keys (Escape
          closes the letter, Cmd+Enter sends it) must not fire from in here. */}
      {editingMine ? (
        <MyMailDialog
          onClose={() => setEditingMine(false)}
          onSaved={() => {
            signatures?.reload();
            senders?.reload();
          }}
        />
      ) : null}

      {askDiscard ? (
        <ConfirmModal
          title={t("drafts.discardTitle")}
          body={t("drafts.discardBody")}
          confirmLabel={t("drafts.discard")}
          cancelLabel={t("common.cancel")}
          onConfirm={discard}
          onClose={() => setAskDiscard(false)}
        />
      ) : null}
    </>
  );

  return createPortal(shell, document.body);
}
